<template>
  <div class="px-6 py-4 px-md-10 py-md-8 fill-height">
    <!--page heading-->
    <v-row justify="start" align="center">
      <v-col class="text-center text-md-start">
        <span class="text-h5 font-weight-medium textLight--text">
          {{ $t("user.publicProfile.employeeProfile") }}
        </span>
      </v-col>
    </v-row>

    <v-container v-if="loading" class="fill-height">
      <v-row>
        <v-col>
          <v-progress-circular indeterminate />
        </v-col>
      </v-row>
    </v-container>

    <v-row v-else-if="publicProfileData">
      <v-col>
        <!--employee info & training statistics-->
        <v-row align="center">
          <!--employee info-->
          <v-col cols="12" md>
            <v-card
              flat
              :color="$vuetify.breakpoint.smAndDown ? 'transparent' : ''"
              class="rounded-lg mouse-cursor px-3"
            >
              <v-row justify="center" justify-md="start">
                <v-col
                  cols="12"
                  md="auto"
                  class="pe-md-0 d-flex d-md-block justify-center justify-start"
                >
                  <v-img
                    class="rounded-lg no-filter"
                    max-width="96"
                    height="96"
                    :src="
                      getImage(publicProfileData.user.avatar) ||
                      require('@/app/user-module/assets/images/user-placeholder.png')
                    "
                    lazy-src="@/app/user-module/assets/images/user-placeholder.png"
                    cover
                  />
                </v-col>

                <v-col cols="auto" md align-self="center">
                  <v-row no-gutters>
                    <v-col class="text-center text-md-start">
                      <span
                        class="
                          text-h6
                          font-weight-regular
                          textLight--text
                          text-ellipse-1
                        "
                      >
                        {{
                          `${textAbstract({
                            text: publicProfileData.user.firstName,
                            maxLength: 20,
                            delimiter: "...",
                          })} ${textAbstract({
                            text: publicProfileData.user.lastName,
                            maxLength: 10,
                            delimiter: "...",
                          })}`
                        }}
                      </span>
                    </v-col>
                  </v-row>

                  <v-row
                    v-if="publicProfileData.employee.designation"
                    no-gutters
                  >
                    <v-col class="text-center text-md-start">
                      <span
                        class="
                          caption
                          font-weight-regular
                          textLight--text
                          text-ellipse-1
                        "
                        style="opacity: 0.5"
                      >
                        {{ publicProfileData.employee.designation }}
                      </span>
                    </v-col>
                  </v-row>

                  <v-row
                    v-if="publicProfileData.employee.organization"
                    no-gutters
                  >
                    <v-col class="text-center text-md-start">
                      <span
                        class="
                          caption
                          font-weight-regular
                          textLight--text
                          text-ellipse-1
                        "
                      >
                        {{
                          getLocalisedValue(
                            publicProfileData.employee.organization.name
                          )
                        }}
                      </span>
                    </v-col>
                  </v-row>
                </v-col>
              </v-row>
            </v-card>
          </v-col>

          <!--training statistics-->
          <v-col cols="12" md class="mt-3 mt-md-0">
            <v-card flat color="transparent">
              <!--laptop(>960) and larger-->
              <v-row v-if="$vuetify.breakpoint.mdAndUp">
                <v-col
                  v-for="(data, index) in trainingStatistics"
                  :key="`trainingStatistics-${index}`"
                  cols="12"
                  sm="6"
                  class="text-start"
                >
                  <v-card
                    class="d-flex align-center mouse-cursor"
                    flat
                    :color="
                      $vuetify.theme.dark
                        ? data.darkModeColor
                        : data.backgroundColor
                    "
                    style="padding: 9px 12px !important"
                  >
                    <v-icon :color="data.iconColor" size="20">
                      {{ data.icon }}
                    </v-icon>

                    <span
                      class="
                        text-start
                        body-2
                        textLight--text
                        flex-grow-1 flex-shrink-0
                        ps-5
                      "
                      style="opacity: 0.8"
                    >
                      {{ data.text }}
                    </span>

                    <span class="subtitle-1 textLight--text">
                      {{ data.value }}
                    </span>
                  </v-card>
                </v-col>
              </v-row>

              <!--tablet(<960) and smaller-->
              <v-row v-else>
                <v-col>
                  <v-card flat>
                    <v-row no-gutters>
                      <v-col
                        v-for="(data, index) in trainingStatistics"
                        :key="`trainingStatistics-${index}`"
                        :cols="$vuetify.breakpoint.width < 410 ? '6' : '3'"
                      >
                        <div
                          class="
                            d-flex
                            flex-row flex-grow-1
                            fill-height
                            align-start
                          "
                        >
                          <div
                            class="
                              d-flex
                              flex-column
                              justify-center
                              flex-grow-1
                              mouse-cursor
                              ps-3
                              py-3
                            "
                          >
                            <span
                              class="
                                text-h5
                                font-weight-bold
                                textLight--text
                                text-center
                              "
                            >
                              {{ data.value }}
                            </span>

                            <v-icon :color="data.iconColor" small>
                              {{ data.icon }}
                            </v-icon>

                            <span
                              class="body-2 textLight--text text-center"
                              style="font-size: 8px"
                            >
                              {{ data.text }}
                            </span>
                          </div>

                          <div
                            class="ms-3 align-self-center theme--dark"
                            style="
                              width: 1px;
                              height: 70%;
                              background-color: rgba(0, 0, 0, 0.12);
                            "
                            :style="
                              index === trainingStatistics.length - 1 ||
                              (index === 1 && $vuetify.breakpoint.width < 410)
                                ? { visibility: 'hidden' }
                                : ''
                            "
                          />
                        </div>
                      </v-col>
                    </v-row>
                  </v-card>
                </v-col>
              </v-row>
            </v-card>
          </v-col>
        </v-row>

        <!--courses title-->
        <v-row justify="start" align="center" class="pt-3">
          <v-col class="text-start">
            <span class="text-h5 font-weight-medium textLight--text">
              {{ $t("user.publicProfile.completedCourses") }}
            </span>
          </v-col>
        </v-row>

        <!--courses table heading row-->
        <v-row
          v-if="$vuetify.breakpoint.mdAndUp"
          justify="start"
          align="center"
          class="mb-3"
        >
          <v-col class="text-start">
            <v-card
              flat
              class="
                rounded-lg
                px-3
                subtitle-1
                font-weight-regular
                primary--text
              "
            >
              <v-row align="center">
                <v-col>
                  <span>
                    {{ $t("user.publicProfile.courseName") }}
                  </span>
                </v-col>

                <v-col>
                  <span>
                    {{ $t("user.publicProfile.trainer") }}
                  </span>
                </v-col>

                <v-col>
                  <span>
                    {{ $t("user.publicProfile.trainingProvider") }}
                  </span>
                </v-col>

                <v-col cols="2" xl="1">
                  <span>
                    {{ $t("user.publicProfile.expiryDate") }}
                  </span>
                </v-col>

                <v-col cols="2" xl="1">
                  <span>
                    {{ $t("user.publicProfile.certificateDate") }}
                  </span>
                </v-col>

                <v-col cols="1" xl="1" class="text-center">
                  <span>
                    {{ $t("course.certificate.certificate") }}
                  </span>
                </v-col>

                <v-col cols="auto">
                  <v-icon color="success" style="visibility: hidden">
                    mdi-check-circle-outline
                  </v-icon>
                </v-col>
              </v-row>
            </v-card>
          </v-col>
        </v-row>

        <!--courses table content rows-->
        <template v-if="publicProfileData.trainingCertificates.length">
          <v-row
            v-for="(data, index) in publicProfileData.trainingCertificates"
            :key="`publicProfileData.trainingCertificates-${index}`"
            justify="start"
            align="center"
            class="mb-3"
          >
            <v-col class="text-start">
              <TrainingCertificatesTableRow
                :data="data"
                :providerName="subscriberName"
              />
            </v-col>
          </v-row>
        </template>

        <v-row v-else class="my-10">
          <v-col>
            <span class="caption font-weight-light">
              {{ $t("noItems") }}
            </span>
          </v-col>
        </v-row>
      </v-col>
    </v-row>

    <v-container v-else class="fill-height">
      <Error />
    </v-container>
  </div>
</template>

<script>
import TrainingCertificatesTableRow from "@/app/user-module/components/public-profile/TrainingCertificatesTableRow";
import Error from "../../core-module/components/Error.vue";

export default {
  name: "PublicProfilePage",
  components: { TrainingCertificatesTableRow, Error },
  data() {
    return {
      loading: true,
      publicProfileData: null,
    };
  },
  computed: {
    trainingStatistics() {
      return [
        {
          icon: "mdi-sync",
          iconColor: "primary",
          text: this.$t("user.publicProfile.ongoingCourses"),
          value: this.publicProfileData.trainingStatistics?.ongoingCourses ?? 0,
          backgroundColor: "#DED4FA",
          darkModeColor: "#3807c1",
        },
        {
          icon: "mdi-check-circle-outline",
          iconColor: "accent",
          text: this.$t("user.publicProfile.completedCourses"),
          value:
            this.publicProfileData.trainingStatistics?.completedCourses ?? 0,
          backgroundColor: "#E1FCEF",
          darkModeColor: "#5dbb8e",
        },
        {
          icon: "mdi-alert-outline",
          iconColor: "error",
          text: this.$t("user.publicProfile.aboutToDueCourses"),
          value:
            this.publicProfileData.trainingStatistics?.aboutDueCourses ?? 0,
          backgroundColor: "#FFDCDC",
          darkModeColor: "#f39797",
        },
        {
          icon: "mdi-alert-octagon-outline",
          iconColor: "error",
          text: this.$t("user.publicProfile.assignedCourses"),
          value:
            this.publicProfileData.trainingStatistics?.assignedCourses ?? 0,
          backgroundColor: "#FFC5B3",
          darkModeColor: "#fb7c54",
        },
      ];
    },
    subscriberName() {
      return process.env.VUE_APP_SUBSCRIBER_NAME;

      // let name =
      //   this.publicProfileData.subscriberProfile?.user?.firstName?.trim() ?? "";
      //
      // return name.length
      //   ? `${name} ${
      //       this.publicProfileData.subscriberProfile?.user?.lastName?.trim() ??
      //       ""
      //     }`.trim()
      //   : "-";
    },
  },
  apollo: {
    getPublicProfile() {
      return {
        query: require("../api/query/getPublicProfile.gql"),
        variables: { id: this.$route.params.id },
        result({ data, loading }) {
          if (data?.getPublicProfile) {
            this.publicProfileData = data?.getPublicProfile;
          }

          this.loading = loading;
        },
      };
    },
  },
};
</script>
